import { createSelector } from 'reselect'

import gateway from '../gateway/gateway'
import { isRequestCancelled } from '../services/request'
import { Action as ActionType, Dispatch } from '../types/reducer'
import { GetState, RootState, TemplatesState } from '../types/state'
import { Template, PublicTemplate } from '../types/template'
import { Filters, Type } from '../types/filters'
import { PrivacySetting } from '../types/group'
import { groupsSelector } from './groups'
import { captureException } from '../sentry-config'

export const TOGGLE_TEMPLATES_LOADING = 'templates/TOGGLE_TEMPLATES_LOADING'
export const GET_TEMPLATES_SUCCESS = 'templates/GET_TEMPLATES_SUCCESS'

type ToggleTemplatesLoadingAction = ActionType<typeof TOGGLE_TEMPLATES_LOADING, boolean>
type GetTemplatesSuccessAction = ActionType<typeof GET_TEMPLATES_SUCCESS, PublicTemplate[]>

type Action = ToggleTemplatesLoadingAction | GetTemplatesSuccessAction

type State = Pick<TemplatesState, 'templates' | 'loading'>

const initialState: State = {
  templates: [],
  loading: false,
}

export default function reducer(state: State = initialState, action: Action): State {
  switch (action.type) {
    case TOGGLE_TEMPLATES_LOADING:
      return {
        ...state,
        loading: action.payload,
      }
    case GET_TEMPLATES_SUCCESS:
      return {
        ...state,
        loading: false,
        templates: action.payload,
      }
    default:
      return state
  }
}

export const toggleTemplatesLoading = (loading: boolean): ToggleTemplatesLoadingAction => ({
  type: TOGGLE_TEMPLATES_LOADING,
  payload: loading,
})

export const getTemplatesSuccess = (templates: PublicTemplate[]): GetTemplatesSuccessAction => ({
  type: GET_TEMPLATES_SUCCESS,
  payload: templates,
})

export const getTemplates = (type: Type, filters: Filters) => async (
  dispatch: Dispatch<ToggleTemplatesLoadingAction | GetTemplatesSuccessAction, void>,
  getState: GetState
): Promise<PublicTemplate[]> => {
  const state = getState()
  // @ts-expect-error
  if (!state) return
  const { account, team } = state.currentUser

  if (
    type === Type.TEAM &&
    !account.team_admin &&
    !account.group_id &&
    team.group_privacy_setting === PrivacySetting.GROUP_PUBLIC
  ) {
    return []
  }

  dispatch(toggleTemplatesLoading(true))

  const searchQuery = filters.query?.trim()

  try {
    const query =
      type === Type.PERSONAL
        ? {
            linked_to_team_template: false,
            search: searchQuery,
          }
        : {
            linked_to_team_template: true,
            search: searchQuery,
            group_id: filters.groups.map(({ id }) => id),
          }

    const templates = await gateway.templates.getAllEmailTemplates(query)

    dispatch(getTemplatesSuccess(templates))

    return templates
  } catch (e) {
    if (isRequestCancelled(e)) {
      throw e
    }

    captureException(e)
  } finally {
    dispatch(toggleTemplatesLoading(false))
  }

  return []
}

export const templatesSelector = createSelector(
  (state: RootState) => state.templates.templates,
  groupsSelector,
  (templates, groups): Template[] =>
    templates.map<Template>(({ groups: templateGroups, ...template }) => {
      if (!templateGroups?.length) {
        return template
      }

      const templateGroupIds = templateGroups.map(({ id }) => id)

      return {
        ...template,
        groups: groups.filter(({ id }) => templateGroupIds.includes(id)),
      }
    })
)
